/**
 * Менеджер управления playback боя
 * Вынесено из legacy-app.js для модульности
 * 
 * Управляет воспроизведением боя: countdown, шаги боя, эффекты, анимации
 */

/**
 * @typedef {Object} BattleState
 * @property {Array} timeline - Timeline боя
 * @property {number} currentStep - Текущий шаг
 * @property {number} lastRenderedStep - Последний отрендеренный шаг
 * @property {boolean} isPlaying - Флаг воспроизведения
 * @property {number} speed - Скорость воспроизведения (мс)
 * @property {number} timer - ID таймера
 * @property {string|null} winner - Победитель боя
 * @property {Array} panels - Панели бойцов
 * @property {Array} autoTimeouts - Массив таймеров для очистки
 */

/**
 * @typedef {Object} CountdownOptions
 * @property {number} [interval=500] - Интервал между сообщениями (мс)
 * @property {number} [finalDelay=250] - Задержка после последнего сообщения (мс)
 * @property {boolean} [large=false] - Большой размер
 * @property {boolean} [small=false] - Маленький размер
 * @property {string} [theme] - Тема (fatal)
 */

/**
 * @typedef {Object} BattlePlaybackManager
 * @property {Function} initializeBattleView - Инициализирует вид боя с countdown
 * @property {Function} playBattleStep - Воспроизводит шаг боя
 * @property {Function} renderBattleEffects - Рендерит эффекты боя для текущего шага
 * @property {Function} goToStep - Переходит к указанному шагу
 * @property {Function} stepBattle - Переходит на указанное количество шагов
 * @property {Function} togglePlayback - Переключает воспроизведение/паузу
 * @property {Function} setPlaybackSpeed - Устанавливает скорость воспроизведения
 * @property {Function} showCountdown - Показывает countdown
 * @property {Function} calculateEventDelay - Вычисляет задержку для события
 */

/**
 * Создает менеджер управления playback боя
 * 
 * @param {Object} params - Параметры менеджера
 * @param {BattleState} params.battleState - Состояние боя (w)
 * @param {Function} params.clearBattleCountdown - Функция очистки countdown (Me)
 * @param {Function} params.hideBattleWinnerBanner - Функция скрытия баннера (Be)
 * @param {Function} params.renderBattleView - Функция рендеринга вида боя (fe)
 * @param {Function} params.showBattleWinnerBanner - Функция показа баннера (Un)
 * @param {Function} params.renderPlaybackStage - Функция рендеринга стадии playback (_e)
 * @param {Function} params.renderPlaybackPanels - Функция рендеринга панелей playback (Wn)
 * @param {Function} params.renderPlaybackLog - Функция рендеринга лога playback (Pe)
 * @param {Function} params.renderProgress - Функция рендеринга прогресса (We)
 * @param {Function} params.stopBattleTimer - Функция остановки таймера (ue)
 * @param {Function} params.getPanelById - Функция получения панели по ID
 * @param {Function} params.showCreatureAnimation - Функция показа анимации существа (gt)
 * @param {Function} params.showTeamAnimation - Функция показа анимации команды (bt)
 * @param {Function} params.showDamageNumber - Функция показа числа урона (Vn)
 * @param {Function} params.showTeamDamageNumber - Функция показа числа урона команды (Jn)
 * @param {Object|null} params.battleEnhancementsManager - Менеджер улучшений боя
 * @returns {BattlePlaybackManager} Объект с методами менеджера
 * 
 * @example
 * const playbackManager = createBattlePlaybackManager({
 *   battleState: w,
 *   clearBattleCountdown: Me,
 *   // ... другие параметры
 * });
 * playbackManager.initializeBattleView();
 */
export function createBattlePlaybackManager({
  battleState,
  clearBattleCountdown,
  hideBattleWinnerBanner,
  renderBattleView,
  showBattleWinnerBanner,
  renderPlaybackStage,
  renderPlaybackPanels,
  renderPlaybackLog,
  renderProgress,
  stopBattleTimer,
  getPanelById,
  showCreatureAnimation,
  showTeamAnimation,
  showDamageNumber,
  showTeamDamageNumber,
  battleEnhancementsManager,
}) {
  // Массив для хранения таймеров countdown
  const countdownTimeouts = [];

  /**
   * Добавляет таймер в массив для последующей очистки
   * @param {number} timeoutId - ID таймера
   */
  function addCountdownTimeout(timeoutId) {
    countdownTimeouts.push(timeoutId);
  }

  /**
   * Вычисляет задержку для события боя
   * 
   * @param {Object|null} entry - Запись события из timeline
   * @param {string} entry.event - Тип события (attack, attack_blocked, effect, start, end)
   * @param {boolean} [entry.defeated] - Флаг поражения
   * @param {boolean} [entry.crit] - Флаг критического удара
   * @returns {number} Задержка в миллисекундах (320-900)
   */
  function calculateEventDelay(entry) {
    if (!entry) return 320;
    
    switch (entry.event) {
      case "attack":
      case "attack_blocked":
        return entry.defeated ? 900 : entry.crit ? 820 : 720;
      case "effect":
        return 520;
      case "start":
      case "end":
        return 480;
      default:
        return 320;
    }
  }

  /**
   * Показывает countdown на экране боя
   * 
   * @param {string|string[]} messages - Сообщения для показа (например, ["3", "2", "1", "FIGHT!"])
   * @param {CountdownOptions} [options={}] - Опции отображения
   * @param {Function} [callback=() => {}] - Колбэк после завершения показа
   * 
   * @example
   * showCountdown(["3", "2", "1", "FIGHT!"], { interval: 650, large: true }, () => {
   *   console.log("Countdown finished");
   * });
   */
  function showCountdown(messages, options = {}, callback = () => {}) {
    const countdownEl = document.getElementById("battle-countdown");
    const textEl = countdownEl?.querySelector(".battle-countdown-text");
    
    if (!countdownEl || !textEl || !messages || (Array.isArray(messages) && messages.length === 0)) {
      callback();
      return;
    }
    
    const messageArray = Array.isArray(messages) ? messages : [String(messages)];
    const interval = Number(options.interval) || 500;
    const finalDelay = Number(options.finalDelay) || 250;
    const isLarge = options.large === true;
    const isSmall = options.small === true && !isLarge;
    const isFatal = options.theme === "fatal";
    
    countdownEl.classList.toggle("battle-countdown--large", isLarge);
    countdownEl.classList.toggle("battle-countdown--small", isSmall);
    countdownEl.classList.toggle("battle-countdown--fatal", isFatal);
    countdownEl.style.display = "flex";
    
    let index = 0;
    
    const showNext = () => {
      if (index >= messageArray.length) {
        const timeoutId = setTimeout(() => {
          countdownEl.style.display = "none";
          textEl.textContent = "";
          callback();
        }, finalDelay);
        addCountdownTimeout(timeoutId);
        return;
      }
      
      textEl.textContent = messageArray[index];
      index += 1;
      
      const timeoutId = setTimeout(showNext, interval);
      addCountdownTimeout(timeoutId);
    };
    
    showNext();
  }

  /**
   * Инициализирует вид боя с countdown
   * 
   * Очищает предыдущее состояние, скрывает баннер победителя,
   * сбрасывает шаги и показывает countdown "3, 2, 1, FIGHT!"
   * 
   * @returns {void}
   */
  function initializeBattleView() {
    clearBattleCountdown();
    hideBattleWinnerBanner();
    
    const timeline = battleState.timeline || [];
    if (timeline.length <= 1) return;
    
    battleState.currentStep = 0;
    battleState.lastRenderedStep = -1;
    renderBattleView();
    
    showCountdown(
      ["3", "2", "1", "FIGHT!"],
      { interval: 650, finalDelay: 380, large: true },
      () => {
        playBattleStep(1);
      }
    );
  }

  /**
   * Воспроизводит шаг боя
   * 
   * Обрабатывает текущий шаг timeline, показывает анимации,
   * countdown для атак, и переходит к следующему шагу.
   * При достижении конца показывает баннер победителя.
   * 
   * @param {number} stepIndex - Индекс шага в timeline (начинается с 1)
   * @returns {void}
   */
  function playBattleStep(stepIndex) {
    const timeline = battleState.timeline || [];
    if (!timeline.length) return;
    
    if (stepIndex >= timeline.length) {
      // Бой завершен
      battleState.currentStep = timeline.length - 1;
      renderBattleView();
      showBattleWinnerBanner(battleState.winner);
      renderPlaybackStage(950);
      
      // Показываем улучшенный эффект победы
      try {
        if (battleEnhancementsManager) {
          battleEnhancementsManager.showVictory();
        }
      } catch (error) {
        console.warn('[battle] Victory effect failed:', error);
      }
      return;
    }
    
    const currentEntry = timeline[stepIndex]?.entry || null;
    const nextEntry = timeline[stepIndex + 1]?.entry || null;
    
    const isFatal = currentEntry &&
      currentEntry.event === "attack" &&
      currentEntry.defeated === true &&
      (!nextEntry || nextEntry.event === "end");
    
    const scheduleNext = (nextStep, delay = null) => {
      const calculatedDelay = delay !== null ? delay : calculateEventDelay(currentEntry);
      const timeoutId = setTimeout(() => {
        playBattleStep(nextStep);
      }, calculatedDelay);
      addCountdownTimeout(timeoutId);
    };
    
    const updateAndContinue = () => {
      battleState.currentStep = stepIndex;
      renderBattleView();
      const nextStep = stepIndex + 1;
      if (nextStep < timeline.length) {
        scheduleNext(nextStep);
      } else {
        scheduleNext(nextStep, 620);
      }
    };
    
    if (isFatal) {
      renderPlaybackStage(900);
      showCountdown(
        ["FATALITY!"],
        { interval: 950, finalDelay: 420, large: true, theme: "fatal" },
        updateAndContinue
      );
    } else if (currentEntry && (currentEntry.event === "attack" || currentEntry.event === "attack_blocked")) {
      showCountdown(
        ["1", "2", "3"],
        { interval: 480, finalDelay: 220, small: true },
        updateAndContinue
      );
    } else if (currentEntry && currentEntry.event === "start") {
      const timeoutId = setTimeout(updateAndContinue, 520);
      addCountdownTimeout(timeoutId);
    } else {
      const timeoutId = setTimeout(updateAndContinue, 620);
      addCountdownTimeout(timeoutId);
    }
  }

  /**
   * Рендерит эффекты боя для текущего шага
   */
  function renderBattleEffects() {
    const effectsEl = document.getElementById("battle-playback-effects");
    if (!effectsEl) return;
    
    const timeline = battleState.timeline;
    if (!timeline.length) {
      effectsEl.innerHTML = "";
      battleState.lastRenderedStep = -1;
      return;
    }
    
    const currentStep = Math.min(Math.max(battleState.currentStep, 0), timeline.length - 1);
    if (currentStep === battleState.lastRenderedStep) return;
    
    battleState.lastRenderedStep = currentStep;
    effectsEl.innerHTML = "";
    
    const entry = timeline[currentStep]?.entry || null;
    if (!entry) return;
    
    renderPlaybackPanels();
    
    const sourcePanel = entry.source != null ? getPanelById(battleState.panels, entry.source) : null;
    const targetPanel = entry.target != null ? getPanelById(battleState.panels, entry.target) : null;
    
    switch (entry.event) {
      case "attack":
      case "attack_blocked":
        renderPlaybackLog("pulse");
        showCreatureAnimation(entry.source, "focus", 650);
        showCreatureAnimation(entry.target, "hit", 550);
        if (sourcePanel) {
          showTeamAnimation(sourcePanel.team, "attack", 600);
        }
        if (targetPanel) {
          showTeamAnimation(targetPanel.team, "hit", 600);
        }
        if (entry.value != null && entry.event === "attack" && entry.value > 0) {
          const damage = Math.max(0, Math.round(entry.value));
          const isCrit = entry.breakdown?.crit === true;
          showDamageNumber(entry.target, damage, isCrit);
          if (targetPanel) {
            showTeamDamageNumber(targetPanel.team, damage, isCrit);
          }
        }
        break;
      case "stack_reset":
        renderPlaybackLog("stack");
        renderPlaybackStage(850);
        break;
      case "evolution":
        renderPlaybackLog("evolution");
        (battleState.panels || []).forEach((panel) => {
          showCreatureAnimation(panel.id, "evolved", 950);
          showTeamAnimation(panel.team, "evolution", 900);
        });
        renderPlaybackStage(900);
        break;
      case "start":
        renderPlaybackLog("flash");
        renderPlaybackStage(650);
        break;
      case "end":
        renderPlaybackLog("flash");
        renderPlaybackStage(650);
        break;
    }
  }

  /**
   * Переходит к указанному шагу в timeline
   * 
   * @param {number} stepIndex - Индекс шага (0-based)
   * @returns {void}
   */
  function goToStep(stepIndex) {
    if (!battleState.timeline.length) return;
    
    const maxStep = battleState.timeline.length - 1;
    battleState.currentStep = Math.max(0, Math.min(stepIndex, maxStep));
    renderBattleView();
    
    if (battleState.currentStep >= maxStep) {
      battleState.isPlaying = false;
      stopBattleTimer();
      renderProgress();
    }
  }

  /**
   * Переходит на указанное количество шагов
   * 
   * @param {number} offset - Смещение (положительное - вперед, отрицательное - назад)
   * @returns {void}
   */
  function stepBattle(offset) {
    stopBattleTimer();
    battleState.isPlaying = false;
    goToStep(battleState.currentStep + offset);
  }

  /**
   * Автоматически воспроизводит следующий шаг
   * 
   * Используется для автоматического воспроизведения боя
   * с заданной скоростью (battleState.speed).
   * 
   * @returns {void}
   */
  function autoPlayNext() {
    stopBattleTimer();
    
    if (battleState.isPlaying) {
      battleState.timer = setTimeout(() => {
        const nextStep = battleState.currentStep + 1;
        if (nextStep >= battleState.timeline.length) {
          battleState.isPlaying = false;
          renderProgress();
          return;
        }
        battleState.currentStep = nextStep;
        renderBattleView();
        autoPlayNext();
      }, battleState.speed);
    }
  }

  /**
   * Переключает воспроизведение/паузу
   * 
   * Если бой завершен, перематывает в начало.
   * 
   * @returns {void}
   */
  function togglePlayback() {
    if (!battleState.timeline.length) return;
    
    battleState.isPlaying = !battleState.isPlaying;
    
    if (battleState.isPlaying && battleState.currentStep >= battleState.timeline.length - 1) {
      battleState.currentStep = 0;
    }
    
    renderProgress();
    
    if (battleState.isPlaying) {
      autoPlayNext();
    } else {
      stopBattleTimer();
    }
  }

  /**
   * Устанавливает скорость воспроизведения
   * 
   * @param {number} speed - Скорость в миллисекундах (минимум 100)
   * @returns {void}
   */
  function setPlaybackSpeed(speed) {
    const speedValue = Number(speed);
    if (Number.isFinite(speedValue)) {
      battleState.speed = Math.max(100, speedValue);
      if (battleState.isPlaying) {
        autoPlayNext();
      }
    }
  }

  return {
    initializeBattleView,
    playBattleStep,
    renderBattleEffects,
    goToStep,
    stepBattle,
    togglePlayback,
    setPlaybackSpeed,
    showCountdown,
    calculateEventDelay,
  };
}

